"use client";

// Core
import { useTranslations } from "next-intl";
import { useRef } from "react";
import { useMediaQuery } from "@mui/material";
import { Link } from "@/i18n/navigation";
import { Swiper, SwiperSlide } from "swiper/react";
import { Grid, Keyboard, Navigation } from "swiper/modules";
import { Swiper as SwiperType } from "swiper";
import { SwiperOptions } from "swiper/types";

// Components
import ProviderCard from "@/components/ProviderCard";

// Hooks
import { useProvidersFetch } from "@/domains/providers/hooks/use-providers-fetch";

// Utils
import st from "./index.module.scss";
import { routes } from "@/definitions/book";

type ProvidersPreviewProps = {
  isBottomMenu?: boolean;
};

export default function ProvidersPreview({ isBottomMenu }: ProvidersPreviewProps) {
  const tSliderNav = useTranslations("SliderNav");
  const tSections = useTranslations("Sections");

  const { providers } = useProvidersFetch({ perPage: 100 });
  const isTabletOrMobile = useMediaQuery("only screen and (max-width: 1024px)");
  const swiperRef = useRef<SwiperType>();

  const SETTINGS_SLIDER: SwiperOptions = {
    slidesPerView: "auto",
    slidesPerGroup: 1,
    watchOverflow: true,
    // keyboard: {{
    //   enabled: true,
    //   onlyInViewport: true,
    // }}
  };

  if (!providers?.length) {
    return null;
  }

  return (
    <section className={`section ${isBottomMenu && st.menu_providers}`}>
      <div className="section__header">
        <h2 className="section__title">{tSections("providers")}</h2>
        <div className="slider_navigation popular">
          {!isTabletOrMobile && (
            <div className="slider_navigation__buttons">
              <button
                className="slider_navigation__btn prev"
                onClick={() => swiperRef.current?.slidePrev()}
              >
                <svg
                  className="slider_navigation__btn_icon"
                  viewBox="0 0 7 12"
                  fill="currentColor"
                  width="18"
                  height="100%"
                >
                  <use href="/images/sprite.svg#arrow-1-left"></use>
                </svg>
              </button>
              <button
                className="slider_navigation__btn next"
                onClick={() => swiperRef.current?.slideNext()}
              >
                <svg
                  className="slider_navigation__btn_icon"
                  viewBox="0 0 7 12"
                  fill="currentColor"
                  width="18"
                  height="100%"
                >
                  <use href="/images/sprite.svg#arrow-1-right"></use>
                </svg>
              </button>
            </div>
          )}

          <Link href={routes.providers} className="button slider_navigation__link">
            {tSliderNav("all")}
            <svg
              className="slider_navigation__btn_icon"
              viewBox="0 0 7 12"
              fill="currentColor"
              width="18"
              height="100%"
            >
              <use href="/images/sprite.svg#arrow-1-right" />
            </svg>
          </Link>
        </div>
      </div>

      {isTabletOrMobile ? (
        <div className={st.providers_list}>
          {providers.map((item, idx) => {
            return <ProviderCard key={`${item.slug}+${idx}`} item={item} />;
          })}
        </div>
      ) : (
        <Swiper
          {...SETTINGS_SLIDER}
          modules={[Navigation, Grid, Keyboard]}
          onBeforeInit={(swiper) => {
            swiperRef.current = swiper;
          }}
        >
          {providers.map((item, idx) => {
            return (
              <SwiperSlide className={st.slide} key={`${item.slug}+${idx}`}>
                <ProviderCard item={item} inSlide />
              </SwiperSlide>
            );
          })}
        </Swiper>
      )}
    </section>
  );
}
